import i18n from 'i18next'

export const MFA_RESEND_COOLDOWN_SECONDS = 60

// Must match MFA_EMAIL_RATE_WINDOW_MS on the backend (60 s).
// Used when the backend returns 429 so the UI cooldown reflects the actual wait.
export const MFA_RATE_LIMIT_COOLDOWN_SECONDS = 60

/**
 * Klucze i18n zwracane przez backend w `message`. Rozpoznajemy błąd po kluczu, nie po treści
 * zdania — patrz `auth.service.ts`. Konwencja z `profile.service.ts`.
 */
const BE_INVALID_CODE = 'beNotifications.errors.mfaInvalidCode'
const BE_CHALLENGE_EXHAUSTED = 'beNotifications.errors.mfaChallengeExhausted'
const BE_CHALLENGE_EXPIRED = 'beNotifications.errors.mfaChallengeExpired'
const BE_CHALLENGE_INVALID = 'beNotifications.errors.mfaChallengeInvalid'
const BE_EMAIL_RATE_LIMIT = 'beNotifications.errors.mfaEmailRateLimit'

/** Limit czasowy zawsze z liczbą sekund. */
const RATE_LIMIT_KEY = 'mfaVerify.rateLimitCountdown'

type TranslateFn = (key: string, options?: Record<string, unknown>) => string

const getRawMessage = (error: unknown): string =>
  typeof error === 'string' ? error : error instanceof Error ? error.message : ''

const getMfaErrorKey = (error: unknown, invalidCodeKey = 'mfaVerify.invalidCode'): string => {
  const raw = getRawMessage(error)

  switch (raw) {
    case BE_INVALID_CODE:
      return invalidCodeKey
    case BE_CHALLENGE_EXHAUSTED:
      return 'mfaVerify.exhaustedError'
    case BE_CHALLENGE_EXPIRED:
    case BE_CHALLENGE_INVALID:
      return 'mfaVerify.sessionExpiredError'
    case BE_EMAIL_RATE_LIMIT:
      return RATE_LIMIT_KEY
  }

  const lowered = raw.toLowerCase()

  // ThrottlerException nie ma konfigurowalnej treści — rozpoznawany po tekście z NestJS.
  // Ta sama gałąź łapie ewentualne nieprzemigrowane komunikaty „Too many ...".
  if (lowered.includes('too many') || lowered.includes('throttl') || lowered.includes('429')) {
    return RATE_LIMIT_KEY
  }

  // Fallback dla endpointów setupu MFA, które nadal zwracają angielskie zdania.
  // DEBT: zmigrować `resolveSetupChallenge` / `enableTotp` / `enableEmailMfa` na klucze i18n.
  if (lowered.includes('session') && (lowered.includes('used') || lowered.includes('expired'))) {
    return 'mfaVerify.sessionExpiredError'
  }
  if (lowered.includes('invalid or expired')) {
    return 'mfaVerify.sessionExpiredError'
  }
  if (lowered.includes('email service') || lowered.includes('service unavailable')) {
    return 'mfaVerify.emailServiceError'
  }
  if (lowered.includes('invalid') && lowered.includes('code')) {
    return invalidCodeKey
  }

  // Nieznany klucz i18n z backendu — użyj go, jeśli istnieje w słowniku.
  if (raw && i18n.exists(raw)) {
    return raw
  }

  return 'mfaVerify.genericError'
}

export const mapMfaError = (error: unknown, t: TranslateFn, invalidCodeKey = 'mfaVerify.invalidCode'): string => {
  const key = getMfaErrorKey(error, invalidCodeKey)
  // Interpolacja tutaj, nie w wywołaniach — inaczej każde miejsce obsługi błędu musi o niej pamiętać.
  return key === RATE_LIMIT_KEY ? t(key, { seconds: MFA_RATE_LIMIT_COOLDOWN_SECONDS }) : t(key)
}

/** Wyczerpany licznik prób. Stan trwały — nie mija w czasie, jedynym wyjściem jest nowe wyzwanie. */
export const isMfaChallengeExhausted = (error: unknown): boolean => getRawMessage(error) === BE_CHALLENGE_EXHAUSTED

/** Limit czasowy — mija, więc wywołujący może ustawić odliczanie. Komunikat robi `mapMfaError`. */
export const isMfaRateLimited = (error: unknown): boolean => getMfaErrorKey(error) === RATE_LIMIT_KEY

export const downloadMfaBackupCodes = (codes: string[]) => {
  const blob = new Blob([codes.join('\n')], { type: 'text/plain;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'mfa-backup-codes.txt'
  a.click()
  URL.revokeObjectURL(url)
}
